// New pure types — NO Firebase imports in this file

// Phone/email array entries
export interface PhoneEntry { number: string; label?: string; whatsApp?: boolean; }
export interface EmailEntry { address: string; label?: string; }

// Place of birth.
// STRUCTURED city/state/country are the source of truth (owner fix 2026-07-02);
// `name` is a display-only convenience join that must NEVER be re-parsed back
// into city/state/country (that lossy round-trip mislabelled a country as a
// state — e.g. "Delhi, India" → state="India"). `state` is ONLY ever a state /
// province; a missing state stays '' and is NEVER back-filled with the country.
// lat/lon/tz feed the Astro engine.
//
// Build A (2026-07-06) — the longitude key is `lon` (was `lng`), to MATCH intake
// + the astro engine; a `lng`/`lon` mismatch silently DROPPED longitude on any
// Contacts↔intake↔astro sync. lat/lon/tz are OPTIONAL so a PARTIAL POB (e.g. a
// city known but not yet geocoded) is valid + portable. Legacy docs that still
// carry `lng` are read into `lon` by `hydratePob` (one-way read hydration — no
// data backfill in this change).
export interface PlaceOfBirth {
  name: string;
  city?: string;
  state?: string;
  country?: string;
  lat?: number;
  lon?: number;
  tz?: string;
}

/**
 * PURE — the display-only "City, State, Country" join, skipping empties. This is
 * the ONLY sanctioned way to derive `pob.name`; nothing may parse it back out.
 */
export function pobDisplayName(city?: string, state?: string, country?: string): string {
  return [city, state, country]
    .map((s) => (s ?? '').trim())
    .filter((s) => s.length > 0)
    .join(', ');
}

// Prakriti — 12 canonical types (AV shorthand codes, owner-locked 2026-06-17)
// Format: [Dominant] / [Secondary] — letters before slash = dominant, after = secondary, missing = negligible
// Sama Prakriti (all three equal) excluded — out of AV scope (owner, 2026-06-17).
// Prakriti is a permanent attribute of a person — NOT a fallback for anything.
export type Prakriti =
  // Category A — single dosha dominant (9 types)
  | 'V/PK'  // Vaat dominant; Pitta and Kapha balanced secondary
  | 'V/P'   // Vaat dominant; Pitta second; Kapha negligible
  | 'V/K'   // Vaat dominant; Kapha second; Pitta negligible
  | 'P/VK'  // Pitta dominant; Vaat and Kapha balanced secondary
  | 'P/V'   // Pitta dominant; Vaat second; Kapha negligible
  | 'P/K'   // Pitta dominant; Kapha second; Vaat negligible
  | 'K/VP'  // Kapha dominant; Vaat and Pitta balanced secondary
  | 'K/V'   // Kapha dominant; Vaat second; Pitta negligible
  | 'K/P'   // Kapha dominant; Pitta second; Vaat negligible
  // Category B — two doshas co-dominant, third negligible (3 types)
  | 'VP/K'  // Vaat and Pitta co-dominant; Kapha negligible
  | 'PK/V'  // Pitta and Kapha co-dominant; Vaat negligible
  | 'VK/P'; // Vaat and Kapha co-dominant; Pitta negligible

// Participant roles on a project (AD-009)
export type ParticipantRole =
  | 'primary_client'
  | 'co_client'
  | 'family'
  | 'consultant'
  | 'partner'
  | 'staff';

export interface ParticipantEntry {
  contactId: string;
  role: ParticipantRole;
  relationship?: string;
  addedAt?: string;   // ISO string (Firestore Timestamp serialized)
  addedBy?: string;   // uid
}

// Postal address — stored as a map on the contact doc
export interface ContactAddress {
  street?: string;
  city?: string;
  state?: string;
  pin?: string;
  country?: string;
}

// LiveContact — the new Person Data Hub shape (AD-008)
// Extends/replaces the old single phone/email strings
export interface LiveContact {
  cid: string;
  clientNo: string;
  name: string;
  // New array form (AD-008) — the single-string fields are migration sources
  phones: PhoneEntry[];
  emails: EmailEntry[];
  // Person data — captured once, used by Astro engine + reports
  dob?: string;         // ISO date "YYYY-MM-DD"
  tob?: string;         // HH:MM (24h, NO DST — AV policy)
  pob?: PlaceOfBirth;
  prakriti?: Prakriti;
  // Per-person Health module (astro-subject step ③) — free-text health context
  // read alongside astrology, NEVER diagnosis/prescription. Stays portal-side;
  // NOT synced to the astro engine (health PII stays here — Cleo/Vex).
  health?: string;
  // Postal address (Module 2 — Fix 2)
  address?: ContactAddress;
  // Referral source (Module 2 — Fix 3)
  referredBy?: { contactId: string; name: string; };
  // Groups membership (Module 2 — Family/Organization Groups)
  familyIds?: string[];
  // Astro engine seam (AD-008)
  astroChartRef?: string;
  astroChartCached?: boolean;
  // Legacy fields preserved for backward compat
  location: string;
  archived: boolean;
  trashed: boolean;
  trashedAt: string | null;
  trashedBy: string | null;
}

// Human-readable labels for participant roles
export function participantLabel(role: ParticipantRole): string {
  const LABELS: Record<ParticipantRole, string> = {
    primary_client: 'Primary Client',
    co_client: 'Co-Client',
    family: 'Family Member',
    consultant: 'Consultant',
    partner: 'Channel Partner',
    staff: 'AV Staff',
  };
  return LABELS[role] ?? role;
}

/**
 * Migration helper: pull the legacy single `phone` / `email` string fields
 * into the array form. Called on first save of any contact doc written before
 * Module 2 (all 472 existing client docs). Returns the MERGED array — the
 * caller replaces phones/emails with these and deletes the old fields.
 */
export function migratePhoneField(doc: Record<string, unknown>): {
  phones: PhoneEntry[];
  emails: EmailEntry[];
} {
  const phones: PhoneEntry[] = [];
  const emails: EmailEntry[] = [];

  // Seed from existing array (if any)
  if (Array.isArray(doc.phones)) {
    for (const p of doc.phones as PhoneEntry[]) {
      if (p?.number) phones.push(p);
    }
  }
  // Pull legacy single string if not already in the array
  if (typeof doc.phone === 'string' && doc.phone.trim()) {
    const legacyNum = doc.phone.trim();
    if (!phones.some((p) => p.number === legacyNum)) {
      // whatsApp:true — every AV client's existing phone number IS their
      // WhatsApp number (that's how AV has always communicated).
      phones.push({ number: legacyNum, label: 'primary', whatsApp: true });
    }
  }

  if (Array.isArray(doc.emails)) {
    for (const e of doc.emails as EmailEntry[]) {
      if (e?.address) emails.push(e);
    }
  }
  if (typeof doc.email === 'string' && doc.email.trim()) {
    const legacyAddr = doc.email.trim();
    if (!emails.some((e) => e.address === legacyAddr)) {
      emails.push({ address: legacyAddr, label: 'primary' });
    }
  }

  return { phones, emails };
}

/**
 * Primary phone — returns the first phone number (label='primary' wins if present),
 * then first entry, then the legacy single-string `phone` field, then ''.
 */
export function primaryPhone(contact: {
  phones?: PhoneEntry[];
  phone?: string;
}): string {
  const arr = contact.phones ?? [];
  if (arr.length === 0) return contact.phone ?? '';
  const primary = arr.find((p) => p.label === 'primary');
  return (primary ?? arr[0]).number;
}

/**
 * Primary email — same logic as primaryPhone.
 */
export function primaryEmail(contact: {
  emails?: EmailEntry[];
  email?: string;
}): string {
  const arr = contact.emails ?? [];
  if (arr.length === 0) return contact.email ?? '';
  const primary = arr.find((e) => e.label === 'primary');
  return (primary ?? arr[0]).address;
}

// Type guards
export function isParticipantRole(s: string): s is ParticipantRole {
  return [
    'primary_client', 'co_client', 'family', 'consultant', 'partner', 'staff',
  ].includes(s);
}

export function isPrakriti(s: string): s is Prakriti {
  return [
    'V/PK', 'V/P',  'V/K',
    'P/VK', 'P/V',  'P/K',
    'K/VP', 'K/V',  'K/P',
    'VP/K', 'PK/V', 'VK/P',
  ].includes(s);
}
